import React from 'react';

import { useWindowSize } from '../../hooks/useWindowSize';
import SectionHeader from '../SectionHeader';
import { createSlideComponent } from './utils';
import cx from 'clsx';
import styles from './Category.module.scss';
import { MEDIUM_BREAKPOINT, LARGE_BREAKPOINT } from '../modular-grid/constants';
import { CategoriesWithContentTypes } from '../../types/category/categoryTypes';
import { formatMessage } from '../../utils';
import { CarouselSnap, CarouselInfinite } from '../Carousel';
import { UserSessionProvider } from '../UserSessionProvider';
import { isEqual } from 'src/utils/lodash-alternatives';

export const calculatePercentageWidth = (
  length: number,
  windowWidth: number,
  visiblePartLeft: number,
  visiblePartRight: number,
  edgePadding: number
) => {
  const tilePadding = 2 * edgePadding;
  const containerPadding =
    edgePadding + (visiblePartLeft + visiblePartRight) / 2;
  const spaceForThreeTiles = windowWidth - (3 * tilePadding + containerPadding);
  const possibleTileWidth = spaceForThreeTiles / 3;

  return [1, 2].includes(length)
    ? Math.round(
        ((possibleTileWidth + tilePadding) * length + containerPadding) * 100
      ) / windowWidth
    : 100;
};

export interface CategoryRendererWithSlidesPropTypes {
  category?: CategoriesWithContentTypes;
  locale: string;
  currency?: string;
  onGameTileClick?: (args: object) => void;
  onGameTileInfoClick?: (args: object) => void;
  onRouteTileClick?: (args: object) => void;
  onLinkTileClick?: (args: object) => void;
  onViewAllClick?: ({ path }: { path: string }) => void;
  animation?: boolean;
  visible?: boolean;
  size?: 'small' | 'medium';
  displayInMainSection?: boolean;
  pageTitle?: string;
  loggedIn: boolean;
}

const NO_TITLE_COMPONENT_TYPES = ['jackpot_grid'];

const CategoryRenderer = React.memo(
  (props: CategoryRendererWithSlidesPropTypes) => {
    const {
      category,
      locale,
      currency,
      onGameTileClick,
      onGameTileInfoClick,
      onRouteTileClick,
      onLinkTileClick,
      onViewAllClick,
      animation,
      visible,
      size,
      displayInMainSection,
      pageTitle,
      loggedIn,
    } = props;
    const windowWidth = useWindowSize().width;
    if (!category || !visible) return null;
    const {
      id,
      title,
      description,
      view_all_button,
      view_all_button_visible,
      slides = [],
      component_type,
      overlay,
      tableStartTime,
      staticTexts = {
        closedTables: '',
        nextGame: '',
        comeBackLater: '',
        gameInfoTooltip: '',
      },
      autoplay,
    } = category;

    if (!slides.length && !overlay) {
      return (
        <div
          data-testid="category-placeholder"
          className={styles.placeholder}
        ></div>
      );
    }

    const infoOverlay = {
      title: staticTexts.closedTables,
      description: tableStartTime
        ? formatMessage(staticTexts.nextGame, { startTime: tableStartTime })
        : staticTexts.comeBackLater,
    };

    const onCategoryClick = () =>
      view_all_button && onViewAllClick!({ path: view_all_button.route_link });
    const button = view_all_button_visible
      ? {
          text: view_all_button!.label,
          path: view_all_button!.route_link,
          notIndexed: view_all_button!.notIndexed,
          onClick: onCategoryClick,
        }
      : undefined;
    let percentageWidth = 100;

    if (
      category.component_type === 'game_grid' &&
      (category.type === 'Grid' || category.type === 'Single Block') &&
      category.slides
    ) {
      const lastSlide = category.slides[category.slides.length - 1];
      if (lastSlide && lastSlide.gameList) {
        const tilesInLastSlide = lastSlide.gameList.length;
        const expectedAmount = lastSlide.gamesLimit || 3;
        percentageWidth =
          tilesInLastSlide < expectedAmount
            ? calculatePercentageWidth(tilesInLastSlide, windowWidth, 20, 30, 7)
            : 100;
      }
    }

    const componentToRender = createSlideComponent({
      category,
      windowWidth,
      percentageWidth,
      onGameTileClick,
      onGameTileInfoClick,
      onRouteTileClick,
      onLinkTileClick,
      overlay: overlay ? infoOverlay : undefined,
      singleSlide:
        windowWidth >= MEDIUM_BREAKPOINT.min ||
        component_type === 'game_collection',
      size,
      locale,
      currency,
      pageTitle,
      gameInfoTooltip: staticTexts.gameInfoTooltip,
    });

    const displayTitle = NO_TITLE_COMPONENT_TYPES.includes(component_type)
      ? ''
      : title;

    let renderedBannerSlidesAmount = 0;

    let categoryHasSlides =
      category && category.slides && category.slides.length > 0;

    if (category.component_type === 'banner' && category.slides) {
      renderedBannerSlidesAmount = category.slides.filter(slide =>
        slide.banner && slide.banner.personalization
          ? slide.banner.personalization.rendered !== false
          : true
      ).length;
    }
    const shouldBeInfinite =
      component_type === 'banner' && windowWidth >= MEDIUM_BREAKPOINT.min;

    return componentToRender && categoryHasSlides ? (
      <UserSessionProvider loggedIn={loggedIn}>
        <div
          data-testid="category-item"
          data-test-title={title}
          key={id}
          className={cx(styles.wrapper, {
            [styles.mainSection]: displayInMainSection,
          })}
        >
          <div
            className={cx(styles.animationWrapper, {
              [styles.animation]: animation,
              [styles.infiniteWrapper]: shouldBeInfinite,
            })}
          >
            {component_type !== 'banner' && (
              <SectionHeader
                heading={displayTitle}
                description={description}
                button={button}
              />
            )}
            {shouldBeInfinite ? (
              <CarouselInfinite
                specifiedSlidesAmount={renderedBannerSlidesAmount}
                autoplay={autoplay}
              >
                {componentToRender}
              </CarouselInfinite>
            ) : (
              <CarouselSnap
                visiblePartLeft={30}
                visiblePartRight={20}
                specifiedSlidesAmount={renderedBannerSlidesAmount}
                lastItemPercentageWidth={percentageWidth}
                edgeSlidesPadding={windowWidth >= LARGE_BREAKPOINT.min ? 8 : 7}
                {...(component_type === 'banner' ? { autoplay } : {})}
              >
                {componentToRender}
              </CarouselSnap>
            )}
          </div>
        </div>
      </UserSessionProvider>
    ) : null;
  },
  (props, nextprops) => isEqual(props, nextprops)
);

export default CategoryRenderer;
